import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../../context/Toast';
import { useUserInfo } from '../../../context/UserInfo';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import {
  ExpenseGroupType,
  useCreateExpenseGroupMutation,
} from '../../../__generated__/graphql';
import { Form } from '../../../../../components/form/Form';
import { Input } from '../../../../../components/form/Input';
import SubmitButton from '../../../../../components/form/SubmitButton';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import * as yup from 'yup';
import './ExpenseGroupCreateModal.css';

type Props = {
  show: boolean;
  onClose: () => void;
  onSave: (createdExpenseGroupId?: string) => void;
};

type FormData = {
  name: string;
  type: ExpenseGroupType;
  tripLocation?: string;
};

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  tripLocation: yup.string().notRequired(),
});

export const ExpenseGroupCreateModal: React.FC<
  React.PropsWithChildren<Props>
> = ({ show, onClose, onSave }) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const { id: currentEmployeeId } = useUserInfo();
  const errorHandler = useMutationErrorHandler();

  const [selectedType, setSelectedType] = useState<ExpenseGroupType>(
    ExpenseGroupType.Group,
  );
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const [createExpenseGroup] = useCreateExpenseGroupMutation();

  const handleSubmit = async (formData: FormData) => {
    setSubmitInProgress(true);
    try {
      const mutationInput = {
        name: formData.name,
        type: selectedType as ExpenseGroupType,
        employeeId: currentEmployeeId,
        ...(selectedType === ExpenseGroupType.Trip && formData.tripLocation
          ? { tripLocation: formData.tripLocation }
          : { tripLocation: null }),
      };

      const result = await createExpenseGroup({
        variables: {
          input: mutationInput,
        },
      });

      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'GraphQL error occurred');
      }

      if (!result.data) {
        throw new Error('No data returned from mutation');
      }

      if (result.data?.expenseGroups?.createExpenseGroup?.error) {
        throw new Error(result.data.expenseGroups.createExpenseGroup.error);
      }

      const createdExpenseGroupId =
        result.data?.expenseGroups?.createExpenseGroup?.recordId;
      addSuccess(t('expenses.expenseGroupCreatedToast'));
      onSave(createdExpenseGroupId || undefined);
      handleClose();
    } catch (error) {
      errorHandler(error);
    } finally {
      setSubmitInProgress(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTypeSelect = (type: ExpenseGroupType) => {
    setSelectedType(type);
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t('expenses.newExpenseGroup')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          defaultValues={{
            type: selectedType || ExpenseGroupType.Group,
            name: '',
            tripLocation: '',
          }}>
          <div className="expense-group-type-selection mb-4">
            <div className="expense-group-type-buttons">
              <button
                type="button"
                className={`expense-group-type-btn ${
                  selectedType === ExpenseGroupType.Trip ? 'selected' : ''
                }`}
                onClick={() => handleTypeSelect(ExpenseGroupType.Trip)}>
                <div className="expense-group-type-icon">
                  <FlairIcon icon="sunny-icon" size="lg" />
                </div>
                <span className="expense-group-type-label">
                  {t('expenses.groups.trip')}
                </span>
              </button>
              <button
                type="button"
                className={`expense-group-type-btn ${
                  selectedType === ExpenseGroupType.Group ? 'selected' : ''
                }`}
                onClick={() => handleTypeSelect(ExpenseGroupType.Group)}>
                <div className="expense-group-type-icon">
                  <FlairIcon icon="folder-outline" size="lg" />
                </div>
                <span className="expense-group-type-label">
                  {t('expenses.groups.group')}
                </span>
              </button>
            </div>
          </div>

          <>
            {selectedType && (
              <Input
                name="name"
                label={t('expenses.expenseGroupName')}
                placeholder={t('expenses.enterExpenseGroupName')}
                required
                className="mb-3"
              />
            )}

            {selectedType === ExpenseGroupType.Trip && (
              <Input
                name="tripLocation"
                label={t('expenses.tripLocation')}
                placeholder={t('expenses.enterTripLocation')}
                required
                className="mb-3"
              />
            )}

            {selectedType && (
              <>
                <input type="hidden" name="type" value={selectedType} />

                <div className="d-flex justify-content-end gap-3 mt-4">
                  <Button variant="outline-secondary" onClick={handleClose}>
                    {t('form.general.cancelButton')}
                  </Button>
                  <SubmitButton disabled={submitInProgress || !selectedType}>
                    {t('form.general.saveButton')}
                  </SubmitButton>
                </div>
              </>
            )}
          </>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
